(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-inspiration-themes-section]';
  const INIT_RETRIES = 40;
  const INIT_INTERVAL_MS = 100;

  const sectionStates = new WeakMap();

  const getSwiperConstructor = () => /** @type {any} */(window).Swiper;

  /** @param {HTMLElement} sectionElement */
  const getSectionState = (sectionElement) => {
    const existing = sectionStates.get(sectionElement);
    if (existing) return existing;

    const state = { swiper: null };
    sectionStates.set(sectionElement, state);
    return state;
  };

  /**
   * @param {HTMLElement} sectionElement
   * @returns {boolean}
   */
  const mountSlider = (sectionElement) => {
    const SwiperConstructor = getSwiperConstructor();
    const swiperEl = sectionElement.querySelector('.inspiration-themes__swiper');

    if (!SwiperConstructor || !swiperEl) return false;

    const state = getSectionState(sectionElement);

    if (state.swiper) {
      state.swiper.update();
      return true;
    }

    const prevBtn = sectionElement.querySelector('[data-slider-prev]');
    const nextBtn = sectionElement.querySelector('[data-slider-next]');

    state.swiper = new SwiperConstructor(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      speed: 500,
      watchOverflow: true,
      grabCursor: true,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      on: {
        /** @param {{ isLocked: boolean }} swiper */
        init(swiper) { toggleLock(sectionElement, swiper); },
        /** @param {{ isLocked: boolean }} swiper */
        slideChange(swiper) { toggleLock(sectionElement, swiper); },
        /** @param {{ isLocked: boolean }} swiper */
        resize(swiper) { toggleLock(sectionElement, swiper); },
      },
    });

    return true;
  };

  /**
   * Hide arrows when all slides fit (isLocked = true in Swiper).
   * @param {HTMLElement} sectionElement
   * @param {{ isLocked: boolean }} swiper
   */
  const toggleLock = (sectionElement, swiper) => {
    const carousel = sectionElement.querySelector('.inspiration-themes__carousel');
    if (!carousel) return;
    carousel.classList.toggle('inspiration-themes__carousel--locked', swiper.isLocked);
  };

  /**
   * @param {HTMLElement} sectionElement
   */
  const animateSection = (sectionElement) => {
    const targets = sectionElement.querySelectorAll(
      '.inspiration-themes__heading-block, .inspiration-themes__card'
    );

    if (window.Shopify?.designMode || !window.IntersectionObserver) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.1 }
    );

    targets.forEach((el) => observer.observe(el));
  };

  /**
   * @param {HTMLElement} sectionElement
   */
  const initializeSection = (sectionElement) => {
    animateSection(sectionElement);

    if (mountSlider(sectionElement)) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (mountSlider(sectionElement) || attempts >= INIT_RETRIES) {
        clearInterval(timer);
      }
    }, INIT_INTERVAL_MS);
  };

  const initializeAllSections = () => {
    const sections = document.querySelectorAll(SECTION_SELECTOR);

    if (!window.IntersectionObserver) {
      sections.forEach((el) => initializeSection(/** @type {HTMLElement} */(el)));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          initializeSection(/** @type {HTMLElement} */(entry.target));
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '300px' }
    );

    sections.forEach((el) => observer.observe(el));
  };

  /** @param {Event} event */
  const handleSectionLoad = (event) => {
    const target = /** @type {HTMLElement} */(event.target);
    const sectionElement = target.matches(SECTION_SELECTOR)
      ? target
      : target.querySelector(SECTION_SELECTOR);

    if (sectionElement) initializeSection(/** @type {HTMLElement} */(sectionElement));
  };

  /** @param {Event} event */
  const handleSectionUnload = (event) => {
    const target = /** @type {HTMLElement} */(event.target);
    const sectionElement = target.matches(SECTION_SELECTOR)
      ? target
      : target.querySelector(SECTION_SELECTOR);

    if (!sectionElement) return;

    const state = sectionStates.get(sectionElement);
    if (!state) return;

    state.swiper?.destroy(true, true);
    state.swiper = null;
    sectionStates.delete(sectionElement);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllSections, { once: true });
  } else {
    initializeAllSections();
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
